import _ from 'lodash';

/**
 * @typedef {Object} TableState
 * @property {Object.<string, any>} [filters]
 * @property {Object} [sorter]
 * @property {string} [sorter.columnKey]
 * @property {'ascend'|'descend'} [sorter.order]
 * @property {Object} [pagination]
 * @property {number} pagination.current
 * @property {number} pagination.pageSize
 * @property {Object.<string, any>} [queryParams]
 */

const STORAGE_KEY_PREFIX = 'appState_';

const SESSION_STORAGE_KEYS = ['auth', 'currentUser', 'company', 'selectedReport'];

const PRESERVED_APP_STATE_PREFIXES = [
  'galleryBookmarks:',
  'galleryBookmarkFilter:',
];

const shouldPreserveAppState = (stateId) => (
  PRESERVED_APP_STATE_PREFIXES.some((prefix) => stateId.startsWith(prefix))
);

/**
 * Clears session-related localStorage entries while preserving gallery bookmarks.
 * @returns {void}
 */
export const clearSessionStorage = () => {
  SESSION_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('filters_') || key.startsWith('savedFilter_')) {
      localStorage.removeItem(key);
      return;
    }

    if (!key.startsWith(STORAGE_KEY_PREFIX)) {
      return;
    }

    const stateId = key.slice(STORAGE_KEY_PREFIX.length);

    if (!shouldPreserveAppState(stateId)) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * @param {string} stateId
 * @param {any} state
 * @returns {void}
 */
export const saveState = (stateId, state) => {
  if (!stateId || _.isEmpty(state)) {
    return;
  }

  const storageKey = `${STORAGE_KEY_PREFIX}${stateId}`;
  localStorage.setItem(storageKey, JSON.stringify(state));
};

/**
 * @param {string} stateId
 * @param {any} defaultState
 * @returns {any}
 */
export const loadState = (stateId, defaultState = {}) => {
  if (!stateId) {
    return defaultState;
  }

  const storageKey = `${STORAGE_KEY_PREFIX}${stateId}`;
  const storedState = localStorage.getItem(storageKey);

  if (!storedState) {
    return defaultState;
  }

  try {
    return JSON.parse(storedState);
  } catch (e) {
    console.error('Error parsing stored state:', e);
    return defaultState;
  }
};

/**
 * @param {string} stateId
 * @returns {void}
 */
export const clearState = (stateId) => {
  if (!stateId) {
    return;
  }

  const storageKey = `${STORAGE_KEY_PREFIX}${stateId}`;
  localStorage.removeItem(storageKey);
};

// Table-specific helpers
export const saveTableState = (tableId, state) => saveState(`table_${tableId}`, state);
export const loadTableState = (tableId, defaultState) => loadState(`table_${tableId}`, defaultState);
export const clearTableState = (tableId) => clearState(`table_${tableId}`); 